const API_URL = process.env.REACT_APP_API_URL || 'https://api.maculanetworks.com';
// Exported for places that need a raw URL (e.g. PDF iframes) rather than a fetch.
export const API_BASE_URL = API_URL;

// Get stored auth token
export const getToken = () => localStorage.getItem('portal_token');

// Set auth token
export const setToken = (token) => localStorage.setItem('portal_token', token);

// Clear auth token (and the active-customer selection so a new user's
// session starts on their default customer, not whoever was previously
// signed in on this device).
export const clearToken = () => {
  localStorage.removeItem('portal_token');
  localStorage.removeItem('portal_user');
  localStorage.removeItem('macula_active_customer_id');
};

// Get stored user info
export const getUser = () => {
  const user = localStorage.getItem('portal_user');
  return user ? JSON.parse(user) : null;
};

// Set user info
export const setUser = (user) => localStorage.setItem('portal_user', JSON.stringify(user));

// Check if token is expired
export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

// Customer switcher — when a portal user has access to multiple
// customers (e.g. one CFO managing two practice locations under the
// same email), the active customer is stored in localStorage and sent
// via X-Macula-Customer-Id on every request. Backend validates against
// the user's accessible_customer_ids list.
const ACTIVE_CUSTOMER_KEY = 'macula_active_customer_id';

export const getActiveCustomerId = () => {
  try { return localStorage.getItem(ACTIVE_CUSTOMER_KEY) || null; }
  catch { return null; }
};

export const setActiveCustomerId = (customerId) => {
  try {
    if (customerId === null || customerId === undefined) {
      localStorage.removeItem(ACTIVE_CUSTOMER_KEY);
    } else {
      localStorage.setItem(ACTIVE_CUSTOMER_KEY, String(customerId));
    }
  } catch { /* localStorage may be disabled — fall through silently */ }
};

// Authorized fetch wrapper
export const authorizedFetch = async (endpoint, options = {}) => {
  const token = getToken();

  if (!token || isTokenExpired(token)) {
    clearToken();
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  const activeCustomerId = getActiveCustomerId();

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...(activeCustomerId ? { 'X-Macula-Customer-Id': activeCustomerId } : {}),
    ...options.headers,
  };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    clearToken();
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  return response;
};

// Public fetch (no auth required)
export const publicFetch = async (endpoint, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  return fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });
};

// Auth API calls
export const authApi = {
  // Step 1: Login with email/password
  login: async (email, password) => {
    const response = await publicFetch('/api/portal/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    return response.json();
  },

  // Step 2: Verify 2FA code
  verify2FA: async (email, code) => {
    const response = await publicFetch('/api/portal/auth/verify-2fa', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
    return response.json();
  },

  // Resend 2FA code
  resend2FA: async (email) => {
    const response = await publicFetch('/api/portal/auth/resend-2fa', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return response.json();
  },

  // Passwordless: request a login link (channel 'email') or one-time code
  // (channel 'sms'). Always resolves to a generic message (no account leak).
  requestLoginLink: async (email, channel = 'email', next = null) => {
    const response = await publicFetch('/api/portal/auth/request-link', {
      method: 'POST',
      body: JSON.stringify({ email, channel, next }),
    });
    return response.json();
  },

  // Passwordless: verify an email magic-link token → { access_token, user }
  verifyLoginLink: async (token) => {
    const response = await publicFetch('/api/portal/auth/verify-link', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    return response.json();
  },

  // Passwordless: verify an SMS one-time code → { access_token, user }
  verifyLoginCode: async (email, code) => {
    const response = await publicFetch('/api/portal/auth/verify-code', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
    return response.json();
  },

  // Validate invitation token
  validateToken: async (token) => {
    const response = await publicFetch(`/api/portal/auth/validate-token?token=${token}`);
    return response.json();
  },

  // Set password (complete registration)
  setPassword: async (token, password, firstName, lastName, phone) => {
    const response = await publicFetch('/api/portal/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({
        token,
        password,
        first_name: firstName,
        last_name: lastName,
        phone,
      }),
    });
    return response.json();
  },

  // Forgot password — sends temp password via SMS
  forgotPassword: async (email) => {
    const response = await publicFetch('/api/portal/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return response.json();
  },

  // Change password (requires auth)
  changePassword: async (currentPassword, newPassword) => {
    const response = await authorizedFetch('/api/portal/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    return response.json();
  },

  // Request phone update — sends code to new number
  updatePhone: async (phone) => {
    const response = await authorizedFetch('/api/portal/auth/update-phone', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });
    return response.json();
  },

  // Verify phone update code
  verifyPhoneUpdate: async (code) => {
    const response = await authorizedFetch('/api/portal/auth/verify-phone-update', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    return response.json();
  },

  // Get current user profile
  getMe: async () => {
    const response = await authorizedFetch('/api/portal/auth/me');
    if (!response.ok) throw new Error('Failed to fetch profile');
    return response.json();
  },
};

// ===========================================================================
// "ME" / COMPANY PROFILE API — read-only Client Profile mirror
// ===========================================================================
export const meApi = {
  // Company info + contacts on file. Returns:
  //   { id, name, phone, address, city, state, zip,
  //     contacts: [{ id, first_name, last_name, title, email, phone,
  //                  is_primary_contact }] }
  getCustomer: async () => {
    const res = await authorizedFetch('/api/portal/me/customer');
    if (!res.ok) throw new Error('Failed to load company info');
    return res.json();
  },

  // Active rate sheet (read-only). Returns
  //   { sheet: { id, effective_date, notes } | null,
  //     entries: [{ service_type_id, service_type_name, rate_fields,
  //                 rate_values, ... }] }
  getRateSheet: async () => {
    const res = await authorizedFetch('/api/portal/me/rate-sheet');
    if (!res.ok) throw new Error('Failed to load rate sheet');
    return res.json();
  },

  // Signed documents grouped by package. Each document includes
  // download_url (null until signed) and signed_at timestamp.
  getDocuments: async () => {
    const res = await authorizedFetch('/api/portal/me/documents');
    if (!res.ok) throw new Error('Failed to load documents');
    return res.json();
  },

  // Files staff has uploaded against this customer and marked
  // client_accessible=TRUE. Separate from the signing-package list.
  getStaffDocuments: async () => {
    const res = await authorizedFetch('/api/portal/me/staff-documents');
    if (!res.ok) throw new Error('Failed to load shared files');
    return res.json();
  },

  // All customers this portal user can switch between. Returns
  //   { effective_customer_id, default_customer_id,
  //     customers: [{id, name, is_effective, is_default}] }
  // The header dropdown only renders when customers.length > 1.
  getAccessibleCustomers: async () => {
    const res = await authorizedFetch('/api/portal/me/customers');
    if (!res.ok) throw new Error('Failed to load customer list');
    return res.json();
  },
};

// Projects API calls
export const reportsApi = {
  // Security assessment reports for the logged-in client.
  // list() -> { reports: [{ id, title, score, band, generated_at, viewed }] }
  list: async () => {
    const res = await authorizedFetch('/api/portal/reports');
    if (!res.ok) throw new Error('Failed to load reports');
    return res.json();
  },
  // get(id) -> { id, title, html, score, band, generated_at }
  get: async (id) => {
    const res = await authorizedFetch(`/api/portal/reports/${id}`);
    if (!res.ok) throw new Error('Failed to load report');
    return res.json();
  },
};

// ===========================================================================
// COMPLIANCE API — HIPAA compliance service status for the portal.
// ===========================================================================
export const complianceApi = {
  // MSA/BAA signing state for the HIPAA tab:
  //   { pending_package: { package_token, status, expires_at,
  //                        documents: [{slug,name,signed}], remaining } | null,
  //     baa: { signed_at, expires_at, expires_in_days } | null,
  //     msa: { signed_at } | null }
  getHipaaDocuments: async () => {
    const res = await authorizedFetch('/api/portal/hipaa-documents');
    if (!res.ok) throw new Error('Failed to load documents');
    return res.json();
  },

  // Status for the logged-in customer.
  //   inactive -> { active: false }
  //   active   -> { active: true, track, service_label,
  //                 summary: { tenant_id, track, chain_ok, last_evidence_at,
  //                            pillars: [{ key, label, status }],   // present|partial|gap
  //                            obligations_due_soon, obligations_overdue,
  //                            baas_expiring,
  //                            latest_clean_report: { generated_at, clean, period } | null,
  //                            evidence_count } }
  getStatus: async () => {
    const res = await authorizedFetch('/api/portal/compliance');
    if (!res.ok) throw new Error('Failed to load compliance status');
    return res.json();
  },

  // Sales-pitch CTA — records that this customer wants to talk about the
  // HIPAA compliance service. NEW endpoint (no existing generic
  // "contact rep" mechanism in the portal to reuse) — backend needs to
  // implement POST /api/portal/compliance/interest.
  expressInterest: async () => {
    const res = await authorizedFetch('/api/portal/compliance/interest', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to send request');
    }
    return res.json();
  },
};

// ===========================================================================
// BADGE API — Badge-In & Screen Lock self-service for the portal.
// Every endpoint is scoped server-side to the portal JWT's
// effective_customer_id — never send a customer_id from the client.
// ===========================================================================

// Shared response handler: surface the backend's `detail` when present.
const badgeJson = async (res, fallbackMsg) => {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    // FastAPI `detail` can be a string (our HTTPExceptions) OR a list of
    // validation objects (422). Never render an object — surface a readable msg.
    let msg = fallbackMsg;
    if (typeof err.detail === 'string') msg = err.detail;
    else if (Array.isArray(err.detail)) msg = err.detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
    else if (err.detail) msg = JSON.stringify(err.detail);
    throw new Error(msg);
  }
  return res.json();
};

export const badgeApi = {
  // Status for the logged-in customer.
  //   inactive -> { active: false }
  //   active   -> { active: true, service_label, activated_at }
  getStatus: async () => {
    const res = await authorizedFetch('/api/portal/badge/status');
    return badgeJson(res, 'Failed to load badge status');
  },

  // Sales-pitch CTA — records that this customer wants to talk about the
  // badge service (mirror of complianceApi.expressInterest; backend
  // implements POST /api/portal/badge/interest).
  expressInterest: async () => {
    const res = await authorizedFetch('/api/portal/badge/interest', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return badgeJson(res, 'Failed to send request');
  },

  // --- Staff roster (badge_persons — the practice's own staff list) ---
  // -> { persons: [{ id, name, employee_id?, active, created_at,
  //                  deactivated_at }] }
  listPersons: async () => {
    const res = await authorizedFetch('/api/portal/badge/persons');
    return badgeJson(res, 'Failed to load staff roster');
  },

  // V3 shape: { first, last, employee_id? }. Called from the scan-to-register
  // modal after a card UID has been captured on a workstation.
  addPerson: async ({ first, last, employee_id }) => {
    const body = { first, last };
    if (employee_id) body.employee_id = employee_id;
    const res = await authorizedFetch('/api/portal/badge/persons', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return badgeJson(res, 'Failed to add employee');
  },

  // Server-side this also deregisters (deactivates) the person's cards.
  deactivatePerson: async (personId) => {
    const res = await authorizedFetch(`/api/portal/badge/persons/${personId}/deactivate`, {
      method: 'POST',
    });
    return badgeJson(res, 'Failed to deactivate person');
  },

  // --- Cards (physically issued by Macula; practice binds UID -> person) ---
  // -> { cards: [{ id, person_id, person_name, card_uid, active,
  //                failed_attempts, locked, created_at, deactivated_at }] }
  listCards: async () => {
    const res = await authorizedFetch('/api/portal/badge/cards');
    return badgeJson(res, 'Failed to load cards');
  },

  assignCard: async (personId, cardUid, pin) => {
    const res = await authorizedFetch(`/api/portal/badge/persons/${personId}/cards`, {
      method: 'POST',
      body: JSON.stringify({ card_uid: cardUid, pin }),
    });
    return badgeJson(res, 'Failed to assign card');
  },

  deactivateCard: async (cardId) => {
    const res = await authorizedFetch(`/api/portal/badge/cards/${cardId}/deactivate`, {
      method: 'POST',
    });
    return badgeJson(res, 'Failed to deactivate card');
  },

  resetCardPin: async (cardId, pin) => {
    const res = await authorizedFetch(`/api/portal/badge/cards/${cardId}/reset-pin`, {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });
    return badgeJson(res, 'Failed to reset PIN');
  },

  // --- Lock policy ---
  // GET -> { policy, effective: { enabled, mode, idle_timeout_seconds },
  //          policy_hash, updated_at }
  getPolicy: async () => {
    const res = await authorizedFetch('/api/portal/badge/policy');
    return badgeJson(res, 'Failed to load lock policy');
  },

  // PUT body { policy: { mode, idle_timeout_seconds, ... } } — sanitized
  // server-side (unknown keys dropped, timeout clamped 30..3600).
  putPolicy: async (policy) => {
    const res = await authorizedFetch('/api/portal/badge/policy', {
      method: 'PUT',
      body: JSON.stringify({ policy }),
    });
    return badgeJson(res, 'Failed to save lock policy');
  },

  // --- Workstations (V3: the customer's MNMON deployments — read-only) ---
  // No enrollment: workstations already run MNMON; the overlay is a
  // config-toggled RMM feature. These rows double as the registration-console
  // options for scan-to-register.
  // -> { workstations: [{ id (deployment id), hostname, last_seen, ... }] }
  listWorkstations: async () => {
    const res = await authorizedFetch('/api/portal/badge/workstations');
    return badgeJson(res, 'Failed to load workstations');
  },

  // --- Card registration mode (scan-to-register — V3) ---
  // Arms a ~2 minute registration window on one of the customer's MNMON
  // workstations: the next card tapped there is captured server-side
  // instead of being treated as an auth attempt.
  // -> { armed: true, expires_at? }
  armRegistration: async (deploymentId) => {
    const res = await authorizedFetch('/api/portal/badge/registration/arm', {
      method: 'POST',
      body: JSON.stringify({ deployment_id: deploymentId }),
    });
    return badgeJson(res, 'Failed to arm the card scan');
  },

  // Poll (~2s) while the modal waits for the tap.
  // -> { armed: bool, captured_uid?: string }
  getRegistrationStatus: async () => {
    const res = await authorizedFetch('/api/portal/badge/registration/status');
    return badgeJson(res, 'Failed to check scan status');
  },

  // --- Sentry overlay config (V3: the customer's account-wide BadgeOverlay
  //     block in their MNMON config — delivered by the MNMON config-flow) ---
  // GET -> { enabled, mode, idle_timeout_seconds } (possibly nested under
  //        `config` — callers tolerate both).
  getOverlayConfig: async () => {
    const res = await authorizedFetch('/api/portal/badge/overlay-config');
    return badgeJson(res, 'Failed to load Sentry settings');
  },

  // PUT { enabled, mode, idle_timeout_seconds } — writes ONLY the
  // BadgeOverlay block of the customer's MNMON config, scoped server-side.
  putOverlayConfig: async ({ enabled, mode, idle_timeout_seconds }) => {
    const res = await authorizedFetch('/api/portal/badge/overlay-config', {
      method: 'PUT',
      body: JSON.stringify({ enabled, mode, idle_timeout_seconds }),
    });
    return badgeJson(res, 'Failed to save Sentry settings');
  },

  // --- Emergency (glass-break) access code — account-wide, WRITE-ONLY ---
  // The code is salted + hashed server-side and can never be read back;
  // GET /overlay-config returns { glass_break_set } for the Set/Not-set
  // status. POST replaces any existing code. -> { set: true }
  setGlassBreakCode: async (code) => {
    const res = await authorizedFetch('/api/portal/badge/glass-break-code', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    return badgeJson(res, 'Failed to set the emergency access code');
  },

  // Disables glass-break on every workstation. -> { set: false }
  removeGlassBreakCode: async () => {
    const res = await authorizedFetch('/api/portal/badge/glass-break-code', {
      method: 'DELETE',
    });
    return badgeJson(res, 'Failed to remove the emergency access code');
  },

  // --- Attribution ---
  // filters: { from, to, limit } (ISO timestamps against session_start)
  // -> { sessions: [{ id, hostname, person_id, person_name, session_start,
  //                   session_end, lock_mode, entry_method, end_reason }] }
  listSessions: async (filters = {}) => {
    const params = [];
    if (filters.from) params.push(`from=${encodeURIComponent(filters.from)}`);
    if (filters.to) params.push(`to=${encodeURIComponent(filters.to)}`);
    if (filters.limit) params.push(`limit=${filters.limit}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    const res = await authorizedFetch(`/api/portal/badge/sessions${qs}`);
    return badgeJson(res, 'Failed to load sessions');
  },

  // -> { break_glass_sessions: [{ id, hostname, person_name, session_start,
  //                               session_end, end_reason }] }
  listBreakGlass: async () => {
    const res = await authorizedFetch('/api/portal/badge/break-glass');
    return badgeJson(res, 'Failed to load break-glass entries');
  },

  // -> { sharing_alerts: [{ id, hostname, person_name, card_id, event_type,
  //                         occurred_at, detail }] }
  listSharingAlerts: async () => {
    const res = await authorizedFetch('/api/portal/badge/sharing-alerts');
    return badgeJson(res, 'Failed to load card-sharing alerts');
  },
};

export const projectsApi = {
  // Get all projects for current user
  getProjects: async () => {
    const response = await authorizedFetch('/api/portal/projects');
    if (!response.ok) throw new Error('Failed to fetch projects');
    return response.json();
  },

  // Get questions for a project
  getQuestions: async (projectId) => {
    const response = await authorizedFetch(`/api/portal/projects/${projectId}/questions`);
    if (!response.ok) throw new Error('Failed to fetch questions');
    return response.json();
  },

  // Submit a response to a question
  submitResponse: async (projectId, questionId, responseText, responseData) => {
    const response = await authorizedFetch(
      `/api/portal/projects/${projectId}/questions/${questionId}/respond`,
      {
        method: 'POST',
        body: JSON.stringify({
          response_text: responseText,
          response_data: responseData,
        }),
      }
    );
    if (!response.ok) throw new Error('Failed to submit response');
    return response.json();
  },

  // Get messages for a project
  getMessages: async (projectId) => {
    const response = await authorizedFetch(`/api/portal/projects/${projectId}/messages`);
    if (!response.ok) throw new Error('Failed to fetch messages');
    return response.json();
  },

  // Send a message on a project
  sendMessage: async (projectId, message, attachments = []) => {
    const response = await authorizedFetch(`/api/portal/projects/${projectId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message, attachments }),
    });
    if (!response.ok) throw new Error('Failed to send message');
    return response.json();
  },

  // Upload a document for a question
  uploadDocument: async (projectId, questionId, file) => {
    const token = getToken();
    if (!token || isTokenExpired(token)) {
      clearToken();
      window.location.href = '/login';
      throw new Error('Session expired');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `${API_URL}/api/portal/projects/${projectId}/questions/${questionId}/upload`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Do NOT set Content-Type - browser sets it with boundary for FormData
        },
        body: formData,
      }
    );

    if (response.status === 401) {
      clearToken();
      window.location.href = '/login';
      throw new Error('Session expired');
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upload document');
    }

    return response.json();
  },

  // List staff-shared deliverables (installers etc.) for a project. Returns
  // released files only, each with `available` / `expired` flags.
  getDeliverables: async (projectId) => {
    const response = await authorizedFetch(`/api/portal/projects/${projectId}/deliverables`);
    if (!response.ok) throw new Error('Failed to fetch downloads');
    return response.json();
  },

  // Get a presigned download URL for one deliverable. Throws with the
  // server's message (e.g. "This download has expired.") on 410.
  downloadDeliverable: async (projectId, deliverableId) => {
    const response = await authorizedFetch(
      `/api/portal/projects/${projectId}/deliverables/${deliverableId}/download`
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'This download is no longer available.');
    }
    return response.json();   // { download_url, file_name }
  },
};

// Signing API calls
export const signingApi = {
  // Get all signing packages for the logged-in user
  getMyPackages: async () => {
    const response = await authorizedFetch('/api/portal/signing/my-packages');
    if (!response.ok) throw new Error('Failed to fetch signing packages');
    return response.json();
  },

  // Get signing package (public - no auth needed for viewing)
  getPackage: async (packageToken) => {
    const response = await publicFetch(`/api/portal/signing/package/${packageToken}`);
    if (!response.ok) throw new Error('Failed to fetch signing package');
    return response.json();
  },

  // Get individual document details (public)
  getDocument: async (signingToken) => {
    const response = await publicFetch(`/api/portal/signing/${signingToken}`);
    if (!response.ok) throw new Error('Failed to fetch document');
    return response.json();
  },

  // Sign a document. Customer packages require portal auth; employee
  // onboarding packages authenticate with the emailed package token instead
  // (pass packageToken — the request then goes out unauthenticated).
  signDocument: async (signingToken, signatureData, signatureType, acceptTerms, formData = null, packageToken = null) => {
    const doFetch = packageToken ? publicFetch : authorizedFetch;
    const response = await doFetch(`/api/portal/signing/${signingToken}/sign`, {
      method: 'POST',
      body: JSON.stringify({
        signature_data: signatureData,
        signature_type: signatureType,
        accept_terms: acceptTerms,
        form_data: formData,
        package_token: packageToken,
      }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to sign document');
    }
    return response.json();
  },

  // Decline signing (same dual auth as signDocument)
  declineDocument: async (signingToken, reason, packageToken = null) => {
    const doFetch = packageToken ? publicFetch : authorizedFetch;
    const response = await doFetch(`/api/portal/signing/${signingToken}/decline`, {
      method: 'POST',
      body: JSON.stringify({ decline_reason: reason, package_token: packageToken }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to decline document');
    }
    return response.json();
  },
};


// ===========================================================================
// BILLING API
// ===========================================================================
export const billingApi = {
  isEnabled: async () => {
    try {
      const res = await authorizedFetch('/api/portal/billing/enabled');
      if (!res.ok) return false;
      const data = await res.json();
      return !!data.enabled;
    } catch {
      return false;
    }
  },

  listInvoices: async (statusFilter = null, year = null) => {
    const params = [];
    if (statusFilter) params.push(`status=${encodeURIComponent(statusFilter)}`);
    if (year) params.push(`year=${year}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    const res = await authorizedFetch(`/api/portal/billing/invoices${qs}`);
    if (!res.ok) throw new Error('Failed to load invoices');
    return res.json();
  },

  getInvoice: async (invoiceId) => {
    const res = await authorizedFetch(`/api/portal/billing/invoices/${invoiceId}`);
    if (!res.ok) throw new Error('Failed to load invoice');
    return res.json();
  },

  getCredit: async () => {
    const res = await authorizedFetch('/api/portal/billing/credit');
    if (!res.ok) return { balance: 0, entries: [] };
    return res.json();
  },

  getInvoicePdfUrl: (invoiceId) => `/api/portal/billing/invoices/${invoiceId}/pdf`,

  // Plain anchor links don't include the portal JWT, so the protected
  // /pdf endpoint 401s and the redirect handler bounces back to dashboard.
  // Fetch with auth, follow any 302 to Wasabi, open the resulting blob.
  openInvoicePdf: async (invoiceId) => {
    const res = await authorizedFetch(`/api/portal/billing/invoices/${invoiceId}/pdf`);
    if (!res?.ok) {
      throw new Error(`Could not load PDF (HTTP ${res?.status})`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  },

  startCheckout: async (invoiceId, method) => {
    const res = await authorizedFetch(`/api/portal/billing/invoices/${invoiceId}/checkout`, {
      method: 'POST',
      body: JSON.stringify({ method }),
    });
    if (!res.ok) throw new Error('Failed to create checkout session');
    return res.json();
  },

  openDispute: async (invoiceId, reason) => {
    const res = await authorizedFetch(`/api/portal/billing/invoices/${invoiceId}/dispute`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to open dispute');
    }
    return res.json();
  },

  listPaymentMethods: async () => {
    const res = await authorizedFetch('/api/portal/billing/payment-methods');
    if (!res.ok) throw new Error('Failed to load payment methods');
    return res.json();
  },

  createSetupSession: async () => {
    const res = await authorizedFetch('/api/portal/billing/payment-methods/setup-session', {
      method: 'POST',
    });
    if (!res.ok) throw new Error('Failed to create setup session');
    return res.json();
  },

  removePaymentMethod: async (pmRowId) => {
    const res = await authorizedFetch(`/api/portal/billing/payment-methods/${pmRowId}`, {
      method: 'DELETE',
    });
    if (!res.ok && res.status !== 204) throw new Error('Failed to remove payment method');
    return true;
  },

  setDefaultPaymentMethod: async (pmRowId) => {
    const res = await authorizedFetch(`/api/portal/billing/payment-methods/${pmRowId}/set-default`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error('Failed to set default');
    return res.json();
  },

  getAutoPay: async () => {
    const res = await authorizedFetch('/api/portal/billing/auto-pay');
    if (!res.ok) throw new Error('Failed to load auto-pay');
    return res.json();
  },

  setAutoPay: async (enabled) => {
    const res = await authorizedFetch('/api/portal/billing/auto-pay', {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
    if (!res.ok) throw new Error('Failed to update auto-pay');
    return res.json();
  },

  listPayments: async () => {
    const res = await authorizedFetch('/api/portal/billing/payments');
    if (!res.ok) throw new Error('Failed to load payment history');
    return res.json();
  },

  annualSummaryPdfUrl: (year) => `/api/portal/billing/annual-summary/${year}`,
};


// ===========================================================================
// PROPOSAL API
// ===========================================================================
export const proposalsApi = {
  // List all proposals for the logged-in customer in an actionable state
  // (proposal / declined / contract / onboarding). Used by Dashboard.
  list: async () => {
    const res = await authorizedFetch('/api/portal/proposals');
    if (!res.ok) return [];
    return res.json();
  },

  // Fetch the proposal payload + lifecycle for a given client_path id
  getProposal: async (pathId) => {
    const res = await authorizedFetch(`/api/portal/proposals/${pathId}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to load proposal');
    }
    return res.json();
  },

  // Accept the proposal — moves stage to "contract"
  // addonChoices: { cloud_backup: true | false, ... } — client's yes/no
  // for each optional add-on offered on the proposal.
  accept: async (pathId, addonChoices = {}) => {
    const res = await authorizedFetch(`/api/portal/proposals/${pathId}/accept`, {
      method: 'POST',
      body: JSON.stringify({ addon_choices: addonChoices }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to accept proposal');
    }
    return res.json();
  },

  // Remediation PLAN — the scope-only (no prices) step before the priced
  // remediation proposal.
  getRemediationPlan: async (pathId) => {
    const res = await authorizedFetch(`/api/portal/remediation-plan/${pathId}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to load remediation plan');
    }
    return res.json();
  },

  // Approve the remediation plan's scope (informational — no billing).
  acceptRemediationPlan: async (pathId) => {
    const res = await authorizedFetch(`/api/portal/remediation-plan/${pathId}/accept`, {
      method: 'POST',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to approve plan');
    }
    return res.json();
  },

  // Decline the proposal — requires a reason
  decline: async (pathId, reason) => {
    const res = await authorizedFetch(`/api/portal/proposals/${pathId}/decline`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to decline proposal');
    }
    return res.json();
  },

  // Comment thread (shared with staff)
  listComments: async (pathId) => {
    const res = await authorizedFetch(`/api/portal/proposals/${pathId}/comments`);
    if (!res.ok) throw new Error('Failed to load comments');
    return res.json();
  },

  postComment: async (pathId, content) => {
    const res = await authorizedFetch(`/api/portal/proposals/${pathId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to post comment');
    }
    return res.json();
  },

  // Onboarding status — drives the contract-signing handoff page.
  // Contract (delivered by parallel Agent 2):
  //   { current_stage, require_autopay, has_payment_method,
  //     ready_for_kickoff, payment_method_summary }
  getOnboardingStatus: async (pathId) => {
    const res = await authorizedFetch(`/api/portal/proposals/${pathId}/onboarding-status`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to load onboarding status');
    }
    return res.json();
  },

  // Look up the active signing package for a proposal/path. Returns
  //   { package_id, package_token, package_status, sign_url, expires_at }
  // or 404 if no package exists yet (Accept hasn't happened or inline create
  // failed). Used by ContractSigning.jsx as a fallback when the user lands
  // on /contract/:id without sign_url in location state (e.g. after refresh).
  getSigningPackage: async (pathId) => {
    const res = await authorizedFetch(`/api/portal/proposals/${pathId}/signing-package`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'No active signing package');
    }
    return res.json();
  },

  // Create a Stripe Checkout setup session for collecting a payment method.
  // Returns { url, mode } where url is the Stripe-hosted page to redirect to.
  setupPaymentMethod: async (pathId) => {
    const res = await authorizedFetch(`/api/portal/proposals/${pathId}/setup-payment-method`, {
      method: 'POST',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to start payment setup');
    }
    return res.json();
  },
};

// Hardware order tracking — fulfillment progress on accepted hardware sales.
export const hardwareOrdersApi = {
  list: async () => {
    const res = await authorizedFetch('/api/portal/hardware-orders');
    if (!res.ok) throw new Error('Failed to load orders');
    const data = await res.json();
    return data.orders || [];
  },
};
